import { FieldViewModel, DatalayersMap, except, filterLayersForType, flattenTypes, groupBy, IDataLayer, LayerViewModel, LocaleString, selectMany, setupTypes, MgrLayer } from "../omnia"
import { IDatalayersService } from "../types"
import { getAvailableGroupings, groupLayers } from "./groups"
import { setupLayers } from "./layers"
import { getLayerText } from "./layerText"
import { LayerGroup, LayerGrouping, LayerScope, State } from "./types"

export function createState(
    layerSvc: IDatalayersService,
    layerViewModelFactory: (l: IDataLayer) => LayerViewModel,
    map: DatalayersMap
): ng.IPromise<State> {

    return layerSvc.getLayerManagerEntries().then(layers => {

        const rootTypes = setupTypes(layerSvc.getLayerTypes(), layers)
        const allTypes = flattenTypes(rootTypes)

        const holdingLayers = setupLayers(layers, layerViewModelFactory)

        const years = Array.from(
            new Set(holdingLayers.map(l => l.year).filter(y => !!y))
        ).sort((a, b) => b - a)

        const crops = Array.from(
            new Map(holdingLayers.map(l => l.vm.crop).filter(c => c?.CropId).map(c => [c.CropId || 0, c])).values()
        ).sort((a, b) => a.CropId - b.CropId)

        const fields = layerSvc.fields.filter(f => f.hasBoundary()).map(f => ({ field: f, selected: true }))

        const fieldGroups = groupBy(fields.map(f => f.field).filter(f => !!f.fieldGroup), f => f.fieldGroup)
            .map(g => ({ group: g.key, fields: g.values, selected: true }))

        const state: State = {

            layerSvc,
            layerViewModelFactory,
            map,

            holdingLayers,

            rootTypes,
            allTypes,
            type: undefined,

            years,
            year: undefined,

            crops,
            cropId: undefined,

            fields,
            fieldGroups,

            layers: [],
            layerGroups: [],

            groupings: [],
            grouping1: undefined,
            grouping2: undefined

        }

        return state

    })

}

export function refreshState(state: State) {

    const { layerSvc, layerViewModelFactory, map, layers } = state

    const visible = map.sortByStackOrder(
        layers.filter(l => l.vm.visible).map(l => l.vm),
        v => v.getMapLayer()
    ).map(v => v.datalayerId)

    return layerSvc.getLayerManagerEntries().then(layers => {

        dispose(state)

        const holdingLayers = setupLayers(layers, layerViewModelFactory)

        state = {
            ...state,
            holdingLayers
        }

        state = restoreCollapsed(state, applyFilters)

        const updatedLayers = new Map(state.layers.map(l => [l.id, l]))

        for (const id of visible) {
            const l = updatedLayers.get(id)
            if (l) {
                l.vm.setVisible(true)
            }
        }

        return state

    })

}


function dispose(state: State) {

    const lvm = state.layers.map(l => l.vm).filter(vm => !!vm)

    for (const vm of lvm) {
        vm.dispose()
    }

}

/**
 * Selects a layer type
 * @param state State
 * @param typeKey Type key
 */
export function selectLayerType(state: State, typeKey: string): State {

    const { allTypes } = state

    const type = allTypes.find(t => t.key == typeKey)

    return applyFilters({ ...state, type })

}

/**
 * Selects/unselects a single field group
 */
export function selectFieldGroup(state: State, group: { group: string, fields: FieldViewModel[], selected: boolean }) {

    const { fields } = state

    let selectedFields = fields.filter(f => f.selected).map(f => f.field)

    selectedFields = group.selected
        ? selectedFields.concat(group.fields)
        : except(selectedFields, group.fields)

    return selectFields(state, selectedFields)

}

/**
 * Selects fields
 */
export function selectFields(state: State, selected: FieldViewModel[]) {

    let { fields, fieldGroups } = state

    const set = new Set(selected.map(f => f.fieldId))

    fields = fields.map(f => ({ ...f, selected: set.has(f.field.fieldId) }))

    fieldGroups = fieldGroups.map(g => ({ ...g, selected: g.fields.some(f => set.has(f.fieldId)) }))

    state = {
        ...state,
        fields,
        fieldGroups
    }

    return restoreCollapsed(state, applyFilters)

}

/**
 * Selects a year
 */
export function selectYear(state: State, year: number): State {
    state = { ...state, year }
    return restoreCollapsed(state, applyFilters)
}

/**
 * Selects a crop
 */
export function selectCropId(state: State, cropId: number): State {
    state = { ...state, cropId }
    return restoreCollapsed(state, applyFilters)
}

/**
 * Applies all current filters (type, year, crop), then groupings, and returns a new state
 * @param state State
 */
function applyFilters(state: State): State {

    const { type, year, cropId, holdingLayers, fields } = state

    const selectedFields = new Set(fields.filter(f => f.selected).map(f => f.field.fieldId))

    const selectedLayers = new Set(state.layers.filter(f => f.selected).map(l => l.id))

    let layers = type
        ? filterLayersForType(holdingLayers, type)
        : []

    layers = type?.layerFilter
        ? layers.filter(type.layerFilter)
        : layers

    layers = year
        ? layers.filter(l => l.year == year)
        : layers

    layers = cropId
        ? layers.filter(l => l.vm.crop?.CropId == cropId)
        : layers

    layers = layers.filter(l => (!l.vm.fieldId) || selectedFields.has(l.vm.fieldId))

    layers = layers.map(l => ({
        ...l,
        selected: selectedLayers.has(l.id),
    }))

    let groupings = restoreGroupings(getAvailableGroupings(layers, state), state.grouping1, state.grouping2)

    groupings = selectDefaultGroupings(groupings)

    return applyGroupings({
        ...state,
        ...groupings,
        layers
    })


}


/**
 * Creates a LayerScope object
 */
function getLayerScope(state: State, groupings: LayerGrouping[]): LayerScope {

    const { type, year, cropId } = state

    const set = new Set((groupings || []).map(g => g.key))

    return {
        type,
        year,
        cropId,
        groupings: {
            crop: set.has("crop"),
            year: set.has("year"),
            type: set.has("layertype"),
            date: set.has("date")
        }
    }
}

/**
 * Sets the text on a set or layers, based on a scope
 */
function setLayerTexts(layers: MgrLayer[], scope: LayerScope): MgrLayer[] {

    return layers.map(l => ({
        ...l,
        text: getLayerText(l, scope)
    }))

}

/**
 * Returns selected layers
 * @param state State
 * @param active If true, returns only active+selected layers. If false, returns only inactive+selected layers. If undefined/null, returns all selected layers.
 */
export function getSelectedLayers(state: State, active: boolean) {

    const { layers } = state || { layers: [] }

    let selection = layers.filter(l => l.selected)

    if (active === false) {
        selection = selection.filter(s => !!s.layer.Inactive)
    }
    else if (active === true) {
        selection = selection.filter(s => !s.layer.Inactive)
    }

    return selection

}

/**
 * Selects/unselects a group
 */
export function selectGroup(state: State, group: LayerGroup, selected: boolean) {

    let { layerGroups } = state

    layerGroups = layerGroups.map(g => {

        if (g !== group) {
            return g
        }

        return {
            ...g,
            selected,
            collapsed: selected ? false : g.collapsed,
            layers: g.layers.map(l => ({ ...l, selected }))
        }

    })

    return getUpdatedState({
        ...state,
        layers: selectMany(layerGroups.map(g => g.layers)),
        layerGroups
    })

}

/**
 * Selects/unselects a layer
 */
export function selectLayer(state: State, group: LayerGroup, layer: MgrLayer, selected: boolean) {

    let { layerGroups } = state

    layerGroups = layerGroups.map(g => {

        if (g !== group) {
            return g
        }

        const layers = g.layers.map(l => l !== layer
            ? l
            : {
                ...l,
                selected
            }
        )

        return {
            ...g,
            layers,
            collapsed: selected ? false : g.collapsed
        }

    })

    return getUpdatedState({
        ...state,
        layers: selectMany(layerGroups.map(g => g.layers)),
        layerGroups
    })

}

export function hasCollapsedGroups(state: State) {
    const { layerGroups } = state || {}
    return layerGroups && layerGroups.some(g => g.collapsed)
}

export function collapseGroups(state: State, collapse: boolean): State {

    let { layerGroups } = state

    layerGroups = layerGroups.map(g =>
        g.collapsed != collapse
            ? { ...g, collapsed: collapse }
            : g
    )

    return {
        ...state,
        layerGroups
    }
}

/**
 * Selects and applies grouping level 1
 * @param state State
 * @param grouping Grouping
 */
export function selectGrouping1(state: State, grouping: LayerGrouping) {
    return selectGroupings(state, grouping, state.grouping2)
}

/**
 * Selects and applies grouping level 2
 * @param state State
 * @param grouping Grouping
 */
export function selectGrouping2(state: State, grouping: LayerGrouping) {
    return selectGroupings(state, state.grouping1, grouping)
}

/**
 * Selects the current grouping levels, returns a new state where {grouping1}, {grouping2} and {layerGroups} have been
 * updated accordingly
 *
 * @param state State
 * @param grouping1 Grouping level 1
 * @param grouping2 Grouping level 2
 */
function selectGroupings(state: State, grouping1: LayerGrouping, grouping2: LayerGrouping) {

    const groupings = restoreGroupings(state.groupings, grouping1, grouping2)

    return applyGroupings({
        ...state,
        ...groupings,
    })

}

/**
 * Applies the groupings currently select, returns a new state with {layerGroups} updates
 *
 * @param state State
 */
function applyGroupings(state: State): State {

    let { layers, grouping1, grouping2 } = state

    const groupings = [grouping1, grouping2].filter(g => !!g)

    layers = setLayerTexts(layers, getLayerScope(state, groupings))

    let layerGroups = groupLayers(layers, groupings)

    layerGroups = layerGroups.filter(g => g.layers.length)

    for (const group of layerGroups) {
        group.selected = group.layers.some(l => l.selected)
        group.collapsed = true
    }

    if ((layerGroups.length == 1) && (!layerGroups[0].name)) {
        layerGroups = [{
            key: "*",
            name: "All layers" as LocaleString,
            layers
        }]
    }

    return getUpdatedState({
        ...state,
        layerGroups
    })

}

/**
 * Saves and restores the "collapsed" state of groups during a transition
 */
function restoreCollapsed(state: State, nextState: (s: State) => State): State {

    const { layerGroups } = state

    const expanded = new Set(layerGroups.filter(g => !g.collapsed).map(g => g.name))

    state = nextState(state)

    state = {
        ...state,
        layerGroups: state.layerGroups.map(g => expanded.has(g.name)
            ? ({ ...g, collapsed: false })
            : g
        )
    }

    return state
}

/**
 * Selects the default grouping to apply
 * @param grouping Grouping info
 * @param layers Layers
 */
function selectDefaultGroupings(grouping: { groupings: LayerGrouping[], grouping1: LayerGrouping, grouping2: LayerGrouping }) {

    const { groupings, grouping1, grouping2 } = grouping

    if (grouping1) {
        return grouping
    }

    // TODO: not sure what the default should be - for now, simply retain the first available grouping
    return {
        ...grouping,
        grouping1: groupings[0]
    }

}

function restoreGroupings(groupings: LayerGrouping[], grouping1: LayerGrouping, grouping2: LayerGrouping) {

    grouping1 = groupings.find(g => g.name == grouping1?.name)
    grouping2 = groupings.find(g => g.name == grouping2?.name)

    grouping1 = grouping1 || grouping2
    grouping2 = (grouping1 === grouping2) ? undefined : grouping2

    return { groupings, grouping1, grouping2 }

}


/**
 * Returns a new version of the state where calculated properties (group.selected, group.selectedCount...) have been updated
 */
function getUpdatedState(state: State): State {

    const { layerGroups } = state

    return {
        ...state,
        layerGroups: layerGroups.map(g => {

            const count = g.layers.filter(l => l.selected).length

            return {
                ...g,
                selected: count > 0,
                selectedCount: count
            }

        })

    }
}
